import { FigmaTypography } from "@libs/zapi-ui/custom";
import { TPreparedClaim } from "../model/types";
import { BookmarkSimple, ShieldCheck, CaretDown, CaretUp, CheckCircle, Check } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useHighlightClaim } from "../lib/useHighlightClaim";
import { useState, useRef, useEffect, useMemo } from "react";
import { NoEvidenceIcon, NotApplicableIcon } from "./_ClaimIcons";
import { Source } from "./_Source";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";

type TProps = {
  claim: TPreparedClaim;
  onReviewToggle: (reviewedReason: "not_applicable" | "closed" | null) => void;
  projectId: string;
  className?: string;
  onGoNext?: () => void;
};

const getClaimBoxStyles = (styles: string) => {
  if (styles.includes("bg-red-50")) {
    return {
      bgColor: "bg-[#fff0f0]",
      borderColor: "border-l-[#8b0c0c]",
      textColor: "text-[#8b0c0c]",
      iconColor: "text-[#985f5f]"
    };
  }
  if (styles.includes("bg-[#FCF5EB]")) {
    return {
      bgColor: "bg-[#FCF5EB]",
      borderColor: "border-l-[#B8842E]",
      textColor: "text-[#B8842E]",
      iconColor: "text-[#c69a5c]"
    };
  }
  if (styles.includes("bg-green-50")) {
    return {
      bgColor: "bg-[#ebf2e7]",
      borderColor: "border-l-[#2b5e30]",
      textColor: "text-[#2b5e30]",
      iconColor: "text-[#5a8a5e]"
    };
  }
  return {
    bgColor: "bg-gray-50",
    borderColor: "border-l-gray-600",
    textColor: "text-gray-900",
    iconColor: "text-gray-600"
  };
};

const ContradictingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M6 1.125C5.03582 1.125 4.09329 1.41091 3.2916 1.94659C2.48991 2.48226 1.86507 3.24363 1.49609 4.13442C1.12711 5.02521 1.03057 6.00541 1.21867 6.95107C1.40678 7.89672 1.87108 8.76536 2.55286 9.44715C3.23464 10.1289 4.10328 10.5932 5.04894 10.7813C5.99459 10.9694 6.97479 10.8729 7.86558 10.5039C8.75637 10.1349 9.51775 9.51009 10.0534 8.7084C10.5891 7.90671 10.875 6.96418 10.875 6C10.8736 4.70749 10.3596 3.46831 9.44564 2.55436C8.5317 1.64042 7.29251 1.12636 6 1.125ZM6 10.125C5.18415 10.125 4.38663 9.88307 3.70827 9.42981C3.02992 8.97655 2.50121 8.33231 2.189 7.57857C1.87679 6.82482 1.7951 5.99542 1.95426 5.19525C2.11343 4.39508 2.5063 3.66008 3.08319 3.08318C3.66008 2.50629 4.39508 2.11342 5.19525 1.95426C5.99543 1.7951 6.82483 1.87679 7.57857 2.189C8.33232 2.50121 8.97655 3.02992 9.42981 3.70827C9.88307 4.38663 10.125 5.18415 10.125 6C10.1238 7.09364 9.68877 8.14213 8.91545 8.91545C8.14213 9.68876 7.09364 10.1238 6 10.125ZM5.625 6.375V3.75C5.625 3.65054 5.66451 3.55516 5.73484 3.48484C5.80516 3.41451 5.90055 3.375 6 3.375C6.09946 3.375 6.19484 3.41451 6.26517 3.48484C6.33549 3.55516 6.375 3.65054 6.375 3.75V6.375C6.375 6.47446 6.33549 6.56984 6.26517 6.64017C6.19484 6.71049 6.09946 6.75 6 6.75C5.90055 6.75 5.80516 6.71049 5.73484 6.64017C5.66451 6.56984 5.625 6.47446 5.625 6.375ZM6.5625 8.0625C6.5625 8.17375 6.52951 8.28251 6.4677 8.37501C6.4059 8.46751 6.31804 8.53961 6.21526 8.58218C6.11248 8.62476 5.99938 8.6359 5.89026 8.61419C5.78115 8.59249 5.68092 8.53891 5.60225 8.46025C5.52359 8.38158 5.47001 8.28135 5.44831 8.17224C5.42661 8.06312 5.43775 7.95002 5.48032 7.84724C5.52289 7.74446 5.59499 7.65661 5.68749 7.5948C5.78 7.53299 5.88875 7.5 6 7.5C6.14919 7.5 6.29226 7.55926 6.39775 7.66475C6.50324 7.77024 6.5625 7.91332 6.5625 8.0625Z"
      fill="#8B0C0C"
    />
  </svg>
);

const getEvidenceIcon = (strength: string | null) => {
  if (strength === "strong") {
    return (
      <div className="bg-[#ebf2e7] rounded-full p-1">
        <ShieldCheck size={12} weight="fill" className="text-[#2b5e30]" />
      </div>
    );
  }
  if (strength?.startsWith("contradicting")) {
    return (
      <div className="bg-[#fff0f0] rounded-full p-1">
        <ContradictingIcon />
      </div>
    );
  }
  if (strength === "weak") {
    return (
      <div className="bg-[#fcf5eb] rounded-full p-1">
        <Check size={12} weight="bold" className="text-[#8a6322]" />
      </div>
    );
  }
  return (
    <div className="bg-[#ececee] rounded-full p-1">
      <NoEvidenceIcon />
    </div>
  );
};

const INITIAL_EVIDENCE_COUNT = 3;
const COUNTDOWN_DELAY = 3;

export function ClaimItem({ claim, onReviewToggle, className, onGoNext }: TProps) {
  const { role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);

  const normalizedClaim = {
    ...claim,
    reviewedReason: claim.reviewedReasonButFrozen ?? claim.reviewedReason
  };

  const { focusClaim } = useHighlightClaim({ claimMarkdown: claim.claimMarkdown });
  const claimBoxStyles = getClaimBoxStyles(claim.styles);
  const strippedClaimText = useMemo(
    () =>
      claim.claimMarkdown
        .replace(/<(?:[^>"']|"[^"]*"|'[^']*')*>|<\/?[a-zA-Z][^>]*/g, "")
        .replace(/\*+/g, "")
        .replace(/^#{1,6}\s+/gm, ""),
    [claim.claimMarkdown]
  );
  const [showAllEvidence, setShowAllEvidence] = useState(false);
  const [expandedEvidenceIndex, setExpandedEvidenceIndex] = useState<number | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownTimersRef = useRef<NodeJS.Timeout[]>([]);

  useEffect(() => {
    setExpandedEvidenceIndex(null);
    setShowAllEvidence(false);
  }, [claim.id]);

  const sourceUrl = normalizedClaim.sourceCitedInMemo || normalizedClaim.detectedSourceId || "";
  const displaySource = sourceUrl;

  const visibleEvidence = showAllEvidence
    ? normalizedClaim.evidence
    : normalizedClaim.evidence.slice(0, INITIAL_EVIDENCE_COUNT);
  const hiddenCount = normalizedClaim.evidence.length - INITIAL_EVIDENCE_COUNT;

  const toggleEvidence = (index: number) => {
    setExpandedEvidenceIndex(expandedEvidenceIndex === index ? null : index);
  };

  const clearCountdownTimers = () => {
    countdownTimersRef.current.forEach(clearTimeout);
    countdownTimersRef.current = [];
  };

  const startCountdown = () => {
    clearCountdownTimers();
    setCountdown(COUNTDOWN_DELAY);

    const timer1 = setTimeout(() => setCountdown(2), 1000);
    const timer2 = setTimeout(() => setCountdown(1), 2000);
    const timer3 = setTimeout(() => {
      setCountdown(null);
      onGoNext?.();
    }, 3000);

    countdownTimersRef.current = [timer1, timer2, timer3];
  };

  const { isReviewedButFrozen, isReviewed } = normalizedClaim;

  const isReviewedState = isReviewedButFrozen !== undefined ? isReviewedButFrozen : isReviewed;

  const isNA =
    normalizedClaim.reviewedReason === "not_applicable" || normalizedClaim.reviewedReasonButFrozen === "not_applicable";
  const showOnlyNAButton = isReviewedState ? isNA : false;

  const isClosed = normalizedClaim.reviewedReason === "closed" || normalizedClaim.reviewedReasonButFrozen === "closed";
  const showOnlyClosedButton = isReviewedState ? isClosed : false;

  const showBothButtons = !showOnlyNAButton && !showOnlyClosedButton;

  const showCountdown = countdown !== null && countdown > 0;

  return (
    <div className={cn("bg-white rounded-xl border border-gray-200", className)} onClick={focusClaim}>
      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className={cn("py-[4px] px-[10px] rounded-[16px] border cursor-default w-fit", claim.styles)}>
            <FigmaTypography variant="label-3">{claim.status}</FigmaTypography>
          </div>
        </div>
        <div className="flex items-start gap-3 justify-between min-w-0">
          <div
            className={cn(
              "flex flex-col gap-1 px-3 py-2 rounded-br rounded-tr border-l-4 min-w-0 flex-1",
              claimBoxStyles.bgColor,
              claimBoxStyles.borderColor
            )}
          >
            <FigmaTypography
              variant="body-2"
              className={cn("text-[12px] leading-[18px] wrap-anywhere", claimBoxStyles.textColor)}
            >
              "{strippedClaimText}"
            </FigmaTypography>
            {displaySource && (
              <div className="flex items-center gap-1 min-w-0">
                <BookmarkSimple size={12} className={cn("shrink-0", claimBoxStyles.iconColor)} weight="fill" />
                <FigmaTypography
                  variant="body-3"
                  className={cn("text-[12px] leading-[18px] wrap-anywhere min-w-0", claimBoxStyles.iconColor)}
                >
                  {displaySource}
                </FigmaTypography>
              </div>
            )}
          </div>
        </div>
        <div className="h-px bg-innoveraZinc-150" />
        {claim.evidence.length > 0 && (
          <>
            <div className="flex flex-col gap-3">
              <div className="flex items-center py-1">
                <FigmaTypography variant="body-2" className="text-[12px] font-medium text-[#18181b]">
                  Evidence
                </FigmaTypography>
              </div>
              <div className="flex flex-col">
                {visibleEvidence.map((evidence, index) => {
                  const isExpanded = expandedEvidenceIndex === index;
                  const isLastItem = index === visibleEvidence.length - 1;
                  return (
                    <div
                      key={evidence.sourceId}
                      className={cn("flex flex-col", !isLastItem && "border-b border-[#ececee]")}
                    >
                      <div className="flex items-center justify-between py-3 gap-2">
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          <div className="min-w-0 overflow-hidden">
                            <Source source={evidence.sourceId} />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getEvidenceIcon(evidence.evidenceStrength)}
                          <button
                            data-testid={`claim-${claim.id}-evidence-${evidence.sourceId}-toggle`}
                            className="flex items-center justify-center rounded-[6px] py-[5px]"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleEvidence(index);
                            }}
                          >
                            {isExpanded ? (
                              <CaretUp size={12} className="text-[#18181b]" />
                            ) : (
                              <CaretDown size={12} className="text-[#18181b]" />
                            )}
                          </button>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="flex flex-col gap-2 pb-3 min-w-0">
                          {evidence.sourceMatchesMemo && (
                            <div className="flex items-center gap-1">
                              <CheckCircle size={12} weight="fill" className="text-[#2a764a]" />
                              <FigmaTypography variant="body-3" className="text-[12px] font-medium text-[#2a764a]">
                                Cited Source
                              </FigmaTypography>
                            </div>
                          )}
                          <FigmaTypography
                            variant="body-3"
                            className="text-[12px] text-[#52525b] leading-[18px] wrap-anywhere"
                          >
                            {evidence.verbatimText}
                          </FigmaTypography>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {hiddenCount > 0 && !showAllEvidence && (
                <button
                  data-testid={`claim-${claim.id}-show-more-evidence-button`}
                  className="flex items-center gap-1 px-2 py-[5px] rounded-[6px]"
                  onClick={() => setShowAllEvidence(true)}
                >
                  <FigmaTypography variant="body-3" className="text-[12px] font-medium text-[#71717a]">
                    Show {hiddenCount} more
                  </FigmaTypography>
                  <CaretDown size={12} className="text-[#71717a]" />
                </button>
              )}
            </div>
            <div className="h-px bg-innoveraZinc-150 mx-[-16px]" />
          </>
        )}
        <div className="flex flex-col gap-3 min-w-0">
          <FigmaTypography variant="body-2" className="text-[12px] font-medium text-[#18181b]">
            Reasoning
          </FigmaTypography>
          <FigmaTypography variant="body-3" className="text-[12px] text-[#18181b] wrap-break-word">
            {claim.rationale || "-"}
          </FigmaTypography>
        </div>
        <div className="h-px bg-innoveraZinc-150 mt-4 mx-[-16px]" />
        {/* <div onClick={(e) => e.stopPropagation()}>
          <CommentSection claimId={claim.id} comments={claim.comments} projectId={projectId} />
        </div> */}
        {/* <div className="h-px bg-innoveraZinc-150" /> */}
        {hasEdit && (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {showOnlyNAButton && (
              <button
                data-testid={`claim-${claim.id}-not-applicable-button`}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1 h-6 px-2 py-[5px] rounded-[6px] border backdrop-blur-md",
                  isNA ? "border-[#a1a1aa] bg-[#f4f4f5]" : "border-[#ececee] bg-none"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isNA) {
                    clearCountdownTimers();
                    setCountdown(null);
                    onReviewToggle(null);
                  } else {
                    onReviewToggle("not_applicable");
                    startCountdown();
                  }
                }}
              >
                <NotApplicableIcon size={12} className={cn(isNA ? "text-[#52525b]" : "text-[#52525b]")} />
                <FigmaTypography variant="label-3" className={cn(isNA ? "text-[#52525b]" : "text-[#52525b]")}>
                  {showCountdown ? `Marking N/A in ${countdown}...` : "N/A"}
                </FigmaTypography>
              </button>
            )}
            {showOnlyClosedButton && (
              <button
                data-testid={`claim-${claim.id}-closed-button`}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1 h-6 px-2 py-[5px] rounded-[6px] border backdrop-blur-md",
                  isClosed ? "border-[#2b5e30] bg-[#ebf2e7]" : "border-[#ececee] bg-none"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isClosed) {
                    clearCountdownTimers();
                    setCountdown(null);
                    onReviewToggle(null);
                  } else {
                    onReviewToggle("closed");
                    startCountdown();
                  }
                }}
              >
                <CheckCircle size={12} className={cn(isClosed ? "text-[#2b5e30]" : "text-[#3f3f46]")} />
                <FigmaTypography variant="label-3" className={cn(isClosed ? "text-[#2b5e30]" : "text-[#3f3f46]")}>
                  {showCountdown ? `Closing in ${countdown}...` : "Closed"}
                </FigmaTypography>
              </button>
            )}
            {showBothButtons && (
              <>
                <button
                  data-testid={`claim-${claim.id}-not-applicable-button`}
                  className="flex-1 flex items-center justify-center gap-1 h-6 px-2 py-[5px] rounded-[6px] border border-[#ececee] backdrop-blur-md"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReviewToggle("not_applicable");
                    startCountdown();
                  }}
                >
                  <NotApplicableIcon size={12} className="text-[#3f3f46]" />
                  <FigmaTypography variant="label-3" className="text-[#3f3f46]">
                    N/A
                  </FigmaTypography>
                </button>
                <button
                  data-testid={`claim-${claim.id}-closed-button`}
                  className="flex-1 flex items-center justify-center gap-1 h-6 px-2 py-[5px] rounded-[6px] border border-[#ececee] backdrop-blur-md"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReviewToggle("closed");
                    startCountdown();
                  }}
                >
                  <CheckCircle size={12} className="text-[#3f3f46]" />
                  <FigmaTypography variant="label-3" className="text-[#3f3f46]">
                    Close
                  </FigmaTypography>
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
